import React, {FC} from "react";
import EnumEditor from "./enum_editor";
import {
  SchemaEnums,
  SchemaPropertyWithOptionalType,
  SchemaOptionalType,
  SchemaType,
} from "common/flowmaster/types/schema";
import PropertiesEditor from "./properties_editor";
import ItemsEditor from "./items_editor";
import {
  ActionDefinition,
  WorkflowTask,
} from "common/flowmaster/types/task_config";
import {
  Prompt,
  WorkflowContext,
  WorkflowInputs,
} from "common/flowmaster/types/workflow";
import {OrganisationId} from "common/types/organisation";
import isOptionalType from "./util/is_optional_type";
import getCoreTypeFromOptionalType from "./util/get_core_type_from_optional_type";

const TypeDetail: FC<{
  property: SchemaPropertyWithOptionalType;
  name?: string;
  isEnum: boolean;
  onEnumUpdate?: (values: SchemaEnums) => void;
  onUpdate: (name: string, property: SchemaPropertyWithOptionalType) => void;
  priorTasks?: WorkflowTask[];
  context?: WorkflowContext;
  inputs?: WorkflowInputs;
  prompts?: Prompt[];
  actionDefinitions?: ActionDefinition[];
  organisationId?: OrganisationId;
  useDataStoreProperties?: boolean;
}> = ({
  property,
  name = "",
  isEnum,
  onUpdate,
  onEnumUpdate = () => {},
  priorTasks,
  context,
  inputs,
  prompts,
  actionDefinitions,
  organisationId,
  useDataStoreProperties = true,
}) => {
  const type = isOptionalType(property.type)
    ? getCoreTypeFromOptionalType(property.type as SchemaOptionalType)
    : (property.type as SchemaType);
  const propNoOptionalType = {
    ...property,
    type,
  };
  if (isEnum) {
    return (
      <EnumEditor
        type={propNoOptionalType.type as "string" | "number"}
        values={propNoOptionalType.enum || []}
        onUpdate={onEnumUpdate}
      />
    );
  } else {
    switch (propNoOptionalType.type) {
      case "object":
        return (
          <PropertiesEditor
            onChange={newProps =>
              onUpdate(name, {...property, properties: newProps})
            }
            properties={propNoOptionalType.properties || {}}
            isNested={true}
            priorTasks={priorTasks}
            context={context}
            inputs={inputs}
            prompts={prompts}
            actionDefinitions={actionDefinitions}
            organisationId={organisationId}
            useDataStoreProperties={useDataStoreProperties}
          />
        );
      case "array":
        return (
          <ItemsEditor
            items={propNoOptionalType.items || {type: "string"}}
            onUpdateItems={items => onUpdate(name, {...property, items})}
            priorTasks={priorTasks}
            context={context}
            inputs={inputs}
            prompts={prompts}
            actionDefinitions={actionDefinitions}
            organisationId={organisationId}
            useDataStoreProperties={useDataStoreProperties}
          />
        );
      default:
        return null;
    }
  }
};

export default TypeDetail;
